import React, { useState, useMemo, useEffect } from 'react';
import { SmartDataTable } from '@src/components/SmartDataTable';
import { useLeadsColumns, useLeadsFormFields, useLeadCardView } from './hooks';
import { usePatientFormFields, CreateForm } from '@src/components/shared/forms';
import { useAppointmentsFormFields } from '../appointments/hooks';
import { Button, Tag, Spin, Tooltip, message } from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  CalendarOutlined,
  DollarOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';
import { useNavigation, useInvalidate } from '@refinedev/core';
import { useFollowUpsFormFields } from '../followups/hooks';

export const LeadsList = () => {
  const invalidate = useInvalidate();
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [createPatientFormVisible, setCreatePatientFormVisible] =
    useState(false);
  const [createAppointmentFormVisible, setCreateAppointmentFormVisible] =
    useState(false);
  const [createTaskFormVisible, setCreateTaskFormVisible] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [autoEditLeadId, setAutoEditLeadId] = useState<string | null>(null);

  const columns = useLeadsColumns({ showCreatedAt: true }); // You can set this to false to hide the column
  const formFields = useLeadsFormFields();
  const { LeadCardView } = useLeadCardView();

  const patientFormFields = usePatientFormFields();
  const appointmentFormFields = useAppointmentsFormFields(
    selectedRecord?.patient_id || undefined
  );
  const taskFormFields = useFollowUpsFormFields();

  // Effect to handle auto-edit lead
  useEffect(() => {
    const storedLeadId = localStorage.getItem('autoEditLeadId');
    const storedTimestamp = localStorage.getItem('autoEditTimestamp');

    if (storedLeadId && storedTimestamp) {
      const timestamp = parseInt(storedTimestamp);
      const currentTime = Date.now();

      // Only process if the timestamp is recent (new tab + slow load: allow 60s)
      if (currentTime - timestamp < 60000) {
        // Clear it from localStorage to prevent it from persisting
        localStorage.removeItem('autoEditLeadId');
        localStorage.removeItem('autoEditTimestamp');

        // Set the auto edit lead ID
        setAutoEditLeadId(storedLeadId);

        // Clear the auto edit ID after a longer delay to ensure the form opens properly
        const timer = setTimeout(() => {
          setAutoEditLeadId(null);
        }, 3000);

        return () => clearTimeout(timer);
      } else {
        // Clear old entries
        localStorage.removeItem('autoEditLeadId');
        localStorage.removeItem('autoEditTimestamp');
      }
    }
  }, []);

  // Function to handle create patient
  const handleCreatePatient = () => {
    setCreatePatientFormVisible(true);
  };

  // Function to handle create appointment
  const handleCreateAppointment = () => {
    setCreateAppointmentFormVisible(true);
  };

  // Function to handle create task
  const handleCreateTask = () => {
    setCreateTaskFormVisible(true);
  };

  // Get patient data from selected record for pre-filling the patient form
  const getPatientInitialValues = () => {
    if (!selectedRecord) return {};

    // Extract patient data from the lead record
    const patientName = selectedRecord.patient_name || '';

    // Handle mobile number with proper null checks
    let patientMobile = '';
    if (selectedRecord.patient_mobile_no) {
      patientMobile = selectedRecord.patient_mobile_no.startsWith('91')
        ? selectedRecord.patient_mobile_no.substring(2)
        : selectedRecord.patient_mobile_no;
    }

    // Split patient name into first and last name
    const nameParts = patientName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    return {
      first_name: firstName,
      last_name: lastName,
      mobile_no: patientMobile,
      lead_id: selectedRecord.id, // Include lead_id in the form
    };
  };

  // Get appointment data from selected record for pre-filling the appointment form
  const getAppointmentInitialValues = () => {
    if (!selectedRecord) return {};

    // Extract patient data from the lead record
    const patientName = selectedRecord.patient_name || '';

    // Handle mobile number with proper null checks
    let patientMobile = '';
    if (selectedRecord.patient_mobile_no) {
      patientMobile = selectedRecord.patient_mobile_no.startsWith('91')
        ? selectedRecord.patient_mobile_no.substring(2)
        : selectedRecord.patient_mobile_no;
    }

    return {
      patient_name: patientName,
      patient_mobile_no: patientMobile,
      appointed_to_user_id: selectedRecord.assigned_to,
      notes: selectedRecord.notes,
      lead_id: selectedRecord.id,
      ...(selectedRecord.patient_id && selectedRecord.patient_id !== 0
        ? { patient_id: selectedRecord.patient_id }
        : {}),
    };
  };

  // Get task data from selected record for pre-filling the task form
  const getTaskInitialValues = () => {
    if (!selectedRecord) return {};

    // Extract patient data from the lead record
    const patientName = selectedRecord.patient_name || '';

    // Handle mobile number with proper null checks
    let patientMobile = '';
    if (selectedRecord.patient_mobile_no) {
      patientMobile = selectedRecord.patient_mobile_no.startsWith('91')
        ? selectedRecord.patient_mobile_no.substring(2)
        : selectedRecord.patient_mobile_no;
    }

    return {
      type: 'LEAD',
      reference_id: selectedRecord.id,
      patient_name: patientName,
      patient_phone: patientMobile,
      remark: selectedRecord.notes,
    };
  };

  // Function to handle patient creation success
  const handlePatientCreated = (data: any) => {
    setCreatePatientFormVisible(false);

    // Update the selected record with the new patient data
    if (selectedRecord && data?.data?.data) {
      const newPatient = data.data.data; // Access the actual patient data

      const updatedRecord = {
        ...selectedRecord,
        patient_id: newPatient.id,
        patient_name:
          `${newPatient.first_name || ''} ${newPatient.last_name || ''}`.trim(),
        patient_mobile_no: newPatient.mobile_no,
      };

      setSelectedRecord(updatedRecord);

      // Force form refresh by updating the key
      setFormKey(prev => prev + 1);

      // Invalidate leads cache to refresh the lead form
      invalidate({
        resource: 'leads',
        invalidates: ['list', 'many'],
      });

      // Also invalidate patients cache to ensure fresh data
      invalidate({
        resource: 'patients',
        invalidates: ['list', 'many'],
      });
    }
  };

  // Function to handle appointment creation success
  const handleAppointmentCreated = (data: any) => {
    setCreateAppointmentFormVisible(false);

    // Update the selected record with the new appointment data
    if (selectedRecord && data?.data?.data) {
      const newAppointment = data.data.data;

      const updatedRecord = {
        ...selectedRecord,
        appointment_id: newAppointment.id,
        status: 'converted', // Update lead status to converted
      };

      setSelectedRecord(updatedRecord);

      // Force form refresh by updating the key
      setFormKey(prev => prev + 1);

      // Invalidate leads cache to refresh the lead form
      invalidate({
        resource: 'leads',
        invalidates: ['list', 'many'],
      });

      // Also invalidate appointments cache to ensure fresh data
      invalidate({
        resource: 'appointments',
        invalidates: ['list', 'many'],
      });
    }
  };

  // Function to handle task creation success
  const handleTaskCreated = (data: any) => {
    setCreateTaskFormVisible(false);

    // Invalidate tasks cache to ensure fresh data
    invalidate({
      resource: 'tasks',
      invalidates: ['list', 'many'],
    });

    // Also invalidate leads cache to refresh the lead form
    invalidate({
      resource: 'leads',
      invalidates: ['list', 'many'],
    });
  };

  // Create custom title component with View Patient button and Due Amount tag
  const createCustomTitle = (patientId: string | number | null) => {
    
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>Edit Lead</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {
            !selectedRecord?.patient_id && ( 
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={handleCreatePatient}
              >
                Create Patient
              </Button>
            )
          }
          <Button
            type="default"
            icon={<CheckSquareOutlined />}
            size="small"
            onClick={handleCreateTask}
          >
            Create Task
          </Button>
          <Button
            type="default"
            icon={<CalendarOutlined />}
            size="small"
            onClick={handleCreateAppointment}
          >
            Create Appointment
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <SmartDataTable
        key={formKey}
        api={{
          resource: 'leads',
        }}
        title="Leads"
        columns={columns}
        search={{
          enabled: true,
          fields: ['patient_name', 'patient_mobile_no', 'id'],
          placeholder: 'Search by patient name, mobile, or ID...',
          debounceMs: 500,
        }}
        createForm={{
          fields: formFields as any,
          title: 'Create Lead',
          submitText: 'Create Lead',
        }}
        editForm={{
          fields: formFields as any,
          title: 'Edit Lead',
          submitText: 'Save Changes',
          customTitle: selectedRecord
            ? createCustomTitle(selectedRecord.patient_id)
            : undefined,
        }}
        formDisplayMode="drawer"
        drawerWidth={1200}
        showCreate={true}
        showEdit={true}
        showDelete={true}
        showView={false}
        pageSize={10}
        showSizeChanger={true}
        showQuickJumper={false}
        // showExport={true}
        showColumnVisibility={true}
        tableProps={{
          scroll: {
            x: 'max-content',
            y: 'calc(100vh - 240px)',
          },
        }}
        rowKey="id"
        // Mobile card configuration
        mobileCardComponent={LeadCardView}
        // Chat configuration
        enableChat={true}
        chatType="LEAD"
        chatTitle="Lead Messages"
        chatPlaceholder="Add a note for lead..."
        isintialScroll={true}
        // Handle edit click to capture selected record
        onEditClick={record => {
          setSelectedRecord(record);
        }}
        autoOpenEditId={autoEditLeadId}
      />

      {/* Patient Creation Form */}
      <CreateForm
        resource="patients"
        fields={patientFormFields}
        title="Create New Patient"
        submitText="Create Patient"
        open={createPatientFormVisible}
        onClose={() => setCreatePatientFormVisible(false)}
        onSuccess={handlePatientCreated}
        formDisplayMode="drawer"
        drawerWidth={800}
        initialValues={getPatientInitialValues()}
      />

      {/* Task Creation Form */}
      <CreateForm
        resource="tasks"
        fields={taskFormFields as any}
        title="Create New Task"
        submitText="Create Task"
        open={createTaskFormVisible}
        onClose={() => setCreateTaskFormVisible(false)}
        onSuccess={handleTaskCreated}
        formDisplayMode="drawer"
        drawerWidth={1200}
        initialValues={getTaskInitialValues()}
      />

      {/* Appointment Creation Form */}
      <CreateForm
        key={`lead-appointment-${selectedRecord?.id ?? 'new'}-${selectedRecord?.patient_id ?? 'none'}`}
        resource="appointments"
        fields={appointmentFormFields as any}
        title="Create New Appointment"
        submitText="Create Appointment"
        open={createAppointmentFormVisible}
        onClose={() => setCreateAppointmentFormVisible(false)}
        onSuccess={handleAppointmentCreated}
        formDisplayMode="drawer"
        drawerWidth={1200}
        initialValues={getAppointmentInitialValues()}
      />
      </>
    
  );
};
